import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  FieldPath,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import { Calculator, Info, TriangleAlert } from 'lucide-react';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Input } from 'components/input';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import {
  LTLItem,
  LTL_THRESHOLDS,
  LTL_UNIT_SYSTEMS,
  QuickQuoteInputs,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { cn } from 'utils/shadcn';

import { checkLTLThresholds } from '../utils/ltlCalculations';
import {
  calculateBestSideLoadOpportunity,
  calculateLTLMetrics,
  calculateTotalHandlingUnits,
  calculateTotalPieces,
  getTotalWeightDisplay,
  resolveMaxStackHeightInches,
} from '../utils/ltlMetrics';
import { resolveLTLStackabilityOptions } from '../utils/ltlStackability';
import {
  applySideLoadSuggestionsWithMerge,
  enableShipmentSideLoadFlagOnAllItems,
} from '../utils/sideLoadApplyMerge';
import { hasExplicitPositiveHandlingUnits } from '../utils/sideLoading';
import { useCubicFeetHeightConfig } from './hooks/useCubicFeetHeightConfig';
import { useIsSideLoadingEnabled } from './hooks/useLTLQuickQuoteConfigFlags';

// Coerce L/W/H from form (number or numeric string) so apply + metrics stay consistent.
function coerceLtlDimension(value: unknown): Undef<number> {
  if (value == null || value === '') {
    return undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const n = parseFloat(String(value));
  return Number.isFinite(n) ? n : undefined;
}

// Keep number coercion strict for non-dimension fields: Number('12abc') -> NaN,
// while coerceLtlDimension intentionally allows parseFloat-style prefixes.
function coerceLtlNumber(value: unknown): Undef<number> {
  if (value == null || value === '') {
    return undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function withCoercedDimensions<T extends LTLItem>(item: T): T {
  return {
    ...item,
    length: coerceLtlDimension(item.length),
    width: coerceLtlDimension(item.width),
    height: coerceLtlDimension(item.height),
    weight: coerceLtlNumber(item.weight),
    handlingUnits: coerceLtlNumber(item.handlingUnits),
    pieces: coerceLtlNumber(item.pieces),
  };
}

function projectSideLoadSignatureItem(
  item: LTLItem,
  options?: {
    includeCanSideLoad?: boolean;
  }
) {
  return {
    itemId: item.itemId,
    parentItemId: item.parentItemId ?? null,
    length: item.length ?? null,
    width: item.width ?? null,
    height: item.height ?? null,
    handlingUnits: item.handlingUnits ?? null,
    weight: item.weight ?? null,
    pieces: item.pieces ?? null,
    splitDepth: item.splitDepth ?? 0,
    ...(options?.includeCanSideLoad
      ? { canSideLoad: item.canSideLoad ?? false }
      : {}),
  };
}

function buildSideLoadOptimizationRunSignature({
  items,
  allowSplitMerge,
  sideLoadEnabled,
  units,
  stackable,
  resolvedStackingEnabled,
  hasItemStackabilityOverride,
  maxStackHeightInches,
  customConfig,
}: {
  items: LTLItem[];
  allowSplitMerge: boolean;
  sideLoadEnabled: boolean;
  units: string;
  stackable: boolean;
  resolvedStackingEnabled: boolean;
  hasItemStackabilityOverride: boolean;
  maxStackHeightInches: number;
  customConfig: Undef<{
    heightThresholdInches: number;
    overrideHeightInches: number;
  }>;
}): string {
  return JSON.stringify({
    items: items.map((item) => projectSideLoadSignatureItem(item)),
    allowSplitMerge,
    sideLoadEnabled,
    units,
    stackable,
    resolvedStackingEnabled,
    hasItemStackabilityOverride,
    maxStackHeightInches,
    customConfig: customConfig ?? null,
  });
}

function buildSideLoadItemsSignature(
  items: LTLItem[],
  options?: {
    includeCanSideLoad?: boolean;
  }
): string {
  return JSON.stringify(
    items.map((item) => projectSideLoadSignatureItem(item, options))
  );
}

interface LTLLinearFeetInputProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  integrationName?: TMS;
  replaceItems?: (items: LTLItem[]) => void;
}

export function LTLLinearFeetInput({
  formMethods,
  integrationName,
  replaceItems,
}: LTLLinearFeetInputProps) {
  const { control, setValue, watch } = formMethods;
  const usesShipmentTerminology =
    integrationName === TMS.Tai || integrationName === TMS.McleodEnterprise;

  const watchedItems = useWatch({ control, name: 'ltl.items' });
  // useWatch can hand back the form's live items array, edited in place, so the reference stays the
  // same while dimensions and weight change. Everything derived from items (linear feet, weight,
  // density, warnings) is memoized, so key it on content or it goes stale: a filled 48x40x48 item
  // kept showing 0 linear feet and the suggestion's weight.
  const itemsKey = JSON.stringify(watchedItems ?? []);
  const items = useMemo(() => JSON.parse(itemsKey) as LTLItem[], [itemsKey]);
  const units =
    useWatch({ control, name: 'ltl.commodity.units' }) ??
    LTL_UNIT_SYSTEMS.IMPERIAL;
  const isMetric = units === LTL_UNIT_SYSTEMS.METRIC;
  const autoCalculate =
    useWatch({ control, name: 'ltl.commodity.autoCalculateLinearFeet' }) ??
    true;
  const stackable =
    useWatch({ control, name: 'ltl.commodity.stackable' }) ?? false;
  const sideLoadEnabled =
    useWatch({ control, name: 'ltl.commodity.sideLoadEnabled' }) ?? false;
  // Split/merge UI exists but stays disabled until product enables allowSplitMerge.
  const allowSplitMerge = false;
  const currentLinearFeet = watch('ltl.commodity.linearFeet');
  const sideLoadRequestId = useRef(0);
  const lastAppliedOptimizerItemsSignature = useRef<Maybe<string>>(null);
  const lastOptimizationRunSignature = useRef<Maybe<string>>(null);
  const wasSideLoadEnabledRef = useRef(sideLoadEnabled);

  const isSideLoadingEnabled = useIsSideLoadingEnabled();

  const customConfig = useCubicFeetHeightConfig();
  const maxStackHeightInches = resolveMaxStackHeightInches(customConfig);
  const stackabilityOptions = useMemo(
    () =>
      resolveLTLStackabilityOptions({
        integrationName,
        stackingEnabled: stackable,
      }),
    [integrationName, stackable]
  );

  const metrics = useMemo(() => {
    return calculateLTLMetrics(items, units, {
      customConfig,
      isItemStackable: stackabilityOptions.isItemStackable,
      maxStackHeightInches,
      useLayoutBoundingBoxForCubicFeet: true,
      stackingEnabled: stackabilityOptions.stackingEnabled,
    });
  }, [items, units, customConfig, stackabilityOptions, maxStackHeightInches]);

  const [displayPcf, setDisplayPcf] = useState<Maybe<number>>(null);

  useEffect(() => {
    if (metrics.pcf > 0) {
      setDisplayPcf(metrics.pcf);
    } else {
      setDisplayPcf(null);
    }
  }, [metrics.pcf]);

  const totalWeightDisplay = getTotalWeightDisplay(metrics.totalWeight, units);
  const totalPieces = useMemo(() => calculateTotalPieces(items), [items]);
  const totalHandlingUnits = useMemo(
    () => calculateTotalHandlingUnits(items),
    [items]
  );
  const hasDensity =
    metrics.totalCubicFeet > 0 &&
    metrics.totalWeight > 0 &&
    (displayPcf ?? metrics.pcf) > 0;
  const hasWeight = metrics.totalWeight > 0;
  const hasHandlingUnits = totalHandlingUnits > 0;
  const hasPieces = totalPieces > 0;
  const densityDisplay = hasDensity
    ? `${(displayPcf ?? metrics.pcf).toFixed(2)} lbs/ft³`
    : '—';
  const totalWeightValue = hasWeight
    ? `${Math.round(totalWeightDisplay).toLocaleString()} ${
        isMetric ? 'kg' : 'lbs'
      }`
    : '—';
  const totalHandlingUnitsValue = hasHandlingUnits
    ? totalHandlingUnits.toLocaleString()
    : '—';
  const totalPiecesValue = hasPieces ? totalPieces.toLocaleString() : '—';
  const warnings = useMemo(() => checkLTLThresholds(metrics), [metrics]);

  // Auto-update linear feet when items change (if auto-calculate is enabled)
  useEffect(() => {
    const isEmptyLinearFeet =
      currentLinearFeet === undefined || currentLinearFeet === null;

    if (autoCalculate && metrics.linearFeet !== currentLinearFeet) {
      setValue('ltl.commodity.linearFeet', metrics.linearFeet);
      return;
    }

    if (!autoCalculate && isEmptyLinearFeet && metrics.linearFeet > 0) {
      setValue('ltl.commodity.linearFeet', metrics.linearFeet);
    }
  }, [autoCalculate, metrics.linearFeet, currentLinearFeet, setValue]);

  const handleToggleAutoCalculate = () => {
    const newValue = !autoCalculate;
    setValue('ltl.commodity.autoCalculateLinearFeet', newValue);

    if (newValue) {
      setValue('ltl.commodity.linearFeet', metrics.linearFeet);
    }
  };

  useEffect(() => {
    const transitionedToDisabled =
      wasSideLoadEnabledRef.current && !sideLoadEnabled;
    wasSideLoadEnabledRef.current = sideLoadEnabled;

    if (!isSideLoadingEnabled || sideLoadEnabled) {
      return;
    }

    if (!transitionedToDisabled) {
      return;
    }

    lastOptimizationRunSignature.current = null;
    lastAppliedOptimizerItemsSignature.current = null;

    const hasPersistedSideLoadFlags = items.some((item) => item.canSideLoad);
    if (!hasPersistedSideLoadFlags) {
      return;
    }

    const resetItems = items.map((item) =>
      item.canSideLoad ? { ...item, canSideLoad: false } : item
    );
    setValue('ltl.items', resetItems, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [isSideLoadingEnabled, items, setValue, sideLoadEnabled]);

  useEffect(() => {
    let isEffectCancelled = false;
    const requestId = sideLoadRequestId.current + 1;
    sideLoadRequestId.current = requestId;

    if (!isSideLoadingEnabled || !sideLoadEnabled) {
      return;
    }
    const hasSideLoadEligibleItems = items.some((item) => {
      const coerced = withCoercedDimensions(item);
      const length = coerced.length ?? 0;
      const width = coerced.width ?? 0;
      const height = coerced.height ?? 0;
      return (
        length > 0 &&
        width > 0 &&
        height > 0 &&
        hasExplicitPositiveHandlingUnits(coerced.handlingUnits) &&
        length !== width
      );
    });
    if (!hasSideLoadEligibleItems) {
      return;
    }

    const runSideLoadOptimization = async () => {
      const currentItemsSignature = buildSideLoadItemsSignature(items);
      const currentItemsFullSignature = buildSideLoadItemsSignature(items, {
        includeCanSideLoad: true,
      });
      const resetSplitDepthForExternalEdit =
        lastAppliedOptimizerItemsSignature.current !== currentItemsSignature;
      const coercedItems = items.map((raw) => {
        const coerced = withCoercedDimensions(raw);
        return {
          ...coerced,
          // When rows are edited outside optimizer output, let all rows re-enter
          // split candidacy from depth 0 on the next run.
          splitDepth: resetSplitDepthForExternalEdit
            ? 0
            : (coerced.splitDepth ?? 0),
        };
      });
      const sourceItems = enableShipmentSideLoadFlagOnAllItems(coercedItems, {
        sideLoadEnabled: true,
      });
      const optimizationRunSignature = buildSideLoadOptimizationRunSignature({
        items: sourceItems,
        allowSplitMerge,
        sideLoadEnabled,
        units,
        stackable,
        resolvedStackingEnabled: stackabilityOptions.stackingEnabled,
        hasItemStackabilityOverride: Boolean(
          stackabilityOptions.isItemStackable
        ),
        maxStackHeightInches,
        customConfig,
      });
      if (lastOptimizationRunSignature.current === optimizationRunSignature) {
        return;
      }
      const sideLoadOpportunity = await Promise.resolve(
        calculateBestSideLoadOpportunity(
          sourceItems,
          units,
          customConfig,
          stackabilityOptions.stackingEnabled,
          {
            allowSplitMerge,
            isItemStackable: stackabilityOptions.isItemStackable,
            maxStackHeightInches,
          }
        )
      );
      if (isEffectCancelled || requestId !== sideLoadRequestId.current) {
        return;
      }
      const suggestionsByItemId = new Map(
        (sideLoadOpportunity?.suggestions ?? []).map(
          (suggestion) => [suggestion.itemId, suggestion.rotatedUnits] as const
        )
      );
      const nextItems = applySideLoadSuggestionsWithMerge(
        sourceItems,
        suggestionsByItemId,
        {
          allowSplitMerge,
        }
      );

      if (isEffectCancelled || requestId !== sideLoadRequestId.current) {
        return;
      }

      const nextItemsSignature = buildSideLoadItemsSignature(nextItems);
      const nextItemsFullSignature = buildSideLoadItemsSignature(nextItems, {
        includeCanSideLoad: true,
      });
      const hasMaterialItemChanges =
        nextItemsSignature !== currentItemsSignature;
      const shouldMarkDirty = hasMaterialItemChanges;

      if (currentItemsFullSignature === nextItemsFullSignature) {
        lastAppliedOptimizerItemsSignature.current = currentItemsSignature;
        lastOptimizationRunSignature.current = optimizationRunSignature;
        return;
      }

      if (replaceItems) {
        replaceItems(nextItems);
      } else {
        setValue('ltl.items', nextItems, {
          shouldDirty: shouldMarkDirty,
          shouldTouch: shouldMarkDirty,
          shouldValidate: shouldMarkDirty,
        });
      }

      nextItems.forEach((row, i) => {
        const field = (suffix: string) =>
          `ltl.items.${i}.${suffix}` as FieldPath<QuickQuoteInputs>;
        setValue(field('length'), row.length, {
          shouldDirty: shouldMarkDirty,
          shouldValidate: false,
        });
        setValue(field('width'), row.width, {
          shouldDirty: shouldMarkDirty,
          shouldValidate: false,
        });
        setValue(field('height'), row.height, {
          shouldDirty: shouldMarkDirty,
          shouldValidate: false,
        });
        setValue(field('handlingUnits'), row.handlingUnits, {
          shouldDirty: shouldMarkDirty,
          shouldValidate: false,
        });
        setValue(field('weight'), row.weight, {
          shouldDirty: shouldMarkDirty,
          shouldValidate: false,
        });
        setValue(field('pieces'), row.pieces, {
          shouldDirty: shouldMarkDirty,
          shouldValidate: false,
        });
        setValue(field('itemId'), row.itemId, {
          shouldDirty: shouldMarkDirty,
          shouldValidate: false,
        });
        setValue(field('parentItemId'), row.parentItemId ?? undefined, {
          shouldDirty: shouldMarkDirty,
          shouldValidate: false,
        });
        setValue(field('splitDepth'), row.splitDepth ?? 0, {
          shouldDirty: shouldMarkDirty,
          shouldValidate: false,
        });
        setValue(field('canSideLoad'), true, {
          shouldDirty: shouldMarkDirty,
          shouldValidate: false,
        });
      });

      lastAppliedOptimizerItemsSignature.current = nextItemsSignature;
      lastOptimizationRunSignature.current =
        buildSideLoadOptimizationRunSignature({
          items: nextItems,
          allowSplitMerge,
          sideLoadEnabled,
          units,
          stackable,
          resolvedStackingEnabled: stackabilityOptions.stackingEnabled,
          hasItemStackabilityOverride: Boolean(
            stackabilityOptions.isItemStackable
          ),
          maxStackHeightInches,
          customConfig,
        });
    };

    void runSideLoadOptimization();
    return () => {
      isEffectCancelled = true;
      sideLoadRequestId.current += 1;
    };
  }, [
    allowSplitMerge,
    customConfig,
    isSideLoadingEnabled,
    items,
    replaceItems,
    setValue,
    sideLoadEnabled,
    stackable,
    stackabilityOptions,
    maxStackHeightInches,
    units,
  ]);

  const hasWarning = warnings.hasAnyWarning;
  const tooltipContent = (
    <div className='max-w-xs space-y-2'>
      <p>
        {usesShipmentTerminology
          ? 'Linear feet is calculated using shipment item dimensions and handling units.'
          : 'Linear feet is calculated using commodity-level dimensions and handling units.'}
      </p>
      <p className='text-xs text-neutral-400'>
        Based on how items fit across a 96" trailer width.
      </p>
      {metrics.totalCubicFeet > 0 && (
        <p className='text-xs'>
          Total cubic feet: {metrics.totalCubicFeet.toFixed(2)} ft³
        </p>
      )}
      {metrics.totalWeight > 0 && (
        <p className='text-xs'>
          Total weight: {Math.round(totalWeightDisplay).toLocaleString()}{' '}
          {isMetric ? 'kg' : 'lbs'}
        </p>
      )}
      {metrics.totalCubicFeet > 0 && metrics.totalWeight > 0 && (
        <p className='text-xs'>
          Total Density (PCF): {(displayPcf ?? metrics.pcf).toFixed(2)} lbs/ft³
        </p>
      )}
    </div>
  );

  return (
    <Flex direction='col' gap='sm' className='w-full mx-0'>
      <Flex align='center' gap='xs' className='w-full mx-0'>
        <Label name='ltl.commodity.linearFeet' required={false}>
          Linear Feet
        </Label>
        <Tooltip delayDuration={10}>
          <TooltipTrigger asChild>
            <Info
              className={cn(
                'h-4 w-4 cursor-help',
                hasWarning ? 'text-warning-500' : 'text-neutral-400'
              )}
            />
          </TooltipTrigger>
          <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </Flex>

      <Flex gap='xs' className='w-full mx-0'>
        <Controller
          name='ltl.commodity.linearFeet'
          control={control}
          render={({ field }) => (
            <div className='flex-1 relative'>
              <Input
                {...field}
                type='number'
                min={0}
                step={1}
                value={field.value ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(
                    value === '' ? undefined : Math.ceil(Number(value))
                  );
                }}
                disabled={autoCalculate}
                className={cn(
                  'w-full',
                  warnings.exceedsLinearFeet && 'border-warning-500'
                )}
              />
            </div>
          )}
        />
        <Tooltip delayDuration={10}>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='outline'
              size='auto'
              onClick={handleToggleAutoCalculate}
              className='shrink-0 h-8 w-8'
              buttonNamePosthog={null}
            >
              <Calculator
                className={cn(
                  'h-4 w-4',
                  autoCalculate ? 'text-brand-600' : 'text-warning-500'
                )}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
            {autoCalculate
              ? 'Auto-calculate enabled. Click to enter manually.'
              : 'Manual entry. Click to auto-calculate.'}
          </TooltipContent>
        </Tooltip>
      </Flex>

      {usesShipmentTerminology && (
        <Grid cols='2' gap='md' className='w-full mx-0'>
          <div className='col-span-2'>
            <Flex align='center' gap='xs'>
              <Label name='ltl.pcf' required={false}>
                Total Density (PCF)
              </Label>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Info className='h-4 w-4 shrink-0 cursor-help text-neutral-400' />
                </TooltipTrigger>
                <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                  <p>
                    Overall density (lbs/ft³) from total weight and total
                    volume. Used to estimate freight class.
                  </p>
                </TooltipContent>
              </Tooltip>
            </Flex>
            <Input readOnly value={densityDisplay} className='mt-1' />
            <Typography variant='body-xs' className='text-neutral-500 mt-1'>
              Calculated from shipment-level volume and weight.
            </Typography>
          </div>

          <div className='col-span-2'>
            <Flex align='center' gap='xs'>
              <Label name='ltl.totalCubicFeet' required={false}>
                Total Cubic Feet
              </Label>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Info className='h-4 w-4 shrink-0 cursor-help text-neutral-400' />
                </TooltipTrigger>
                <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                  <p>
                    Total shipment volume in cubic feet, using any configured
                    override height rules.
                  </p>
                </TooltipContent>
              </Tooltip>
            </Flex>
            <Input
              readOnly
              value={
                metrics.totalCubicFeet > 0
                  ? metrics.totalCubicFeet.toFixed(2)
                  : '—'
              }
              className='mt-1'
            />
            <Typography variant='body-xs' className='text-neutral-500 mt-1'>
              {customConfig
                ? 'Volume calculated using configured height override rules.'
                : 'Volume calculated from shipment item dimensions and handling units.'}
            </Typography>
          </div>

          <div>
            <Flex align='center' gap='xs'>
              <Label name='ltl.totalWeight' required={false}>
                Total Weight
              </Label>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Info className='h-4 w-4 shrink-0 cursor-help text-neutral-400' />
                </TooltipTrigger>
                <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                  <p>
                    Total shipment weight in{' '}
                    {isMetric ? 'kilograms (kg).' : 'pounds (lbs).'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </Flex>
            <Input readOnly value={totalWeightValue} className='mt-1' />
          </div>

          <div>
            <Flex align='center' gap='xs'>
              <Label name='ltl.totalPieces' required={false}>
                Total Pieces
              </Label>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Info className='h-4 w-4 shrink-0 cursor-help text-neutral-400' />
                </TooltipTrigger>
                <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                  <p>Total number of pieces.</p>
                </TooltipContent>
              </Tooltip>
            </Flex>
            <Input readOnly value={totalPiecesValue} className='mt-1' />
          </div>

          <div className='col-span-2'>
            <Flex align='center' gap='xs'>
              <Label name='ltl.totalHandlingUnits' required={false}>
                Total Handling Units
              </Label>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Info className='h-4 w-4 shrink-0 cursor-help text-neutral-400' />
                </TooltipTrigger>
                <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                  <p>Total number of handling units across all items.</p>
                </TooltipContent>
              </Tooltip>
            </Flex>
            <Input readOnly value={totalHandlingUnitsValue} className='mt-1' />
          </div>
        </Grid>
      )}

      {hasWarning && (
        <div className='p-3 bg-warning-100 border border-warning-500 rounded-md'>
          <Flex align='start' gap='sm'>
            <TriangleAlert className='h-4 w-4 text-warning-600 shrink-0 mt-0.5' />
            <Flex direction='col' gap='xs'>
              <Typography variant='body-sm' weight='medium'>
                Volume Rate May Apply
              </Typography>
              <Typography variant='body-xs'>
                {usesShipmentTerminology ? 'Shipments' : 'Commodities'}{' '}
                exceeding {LTL_THRESHOLDS.MAX_CUBIC_FEET} cubic feet,{' '}
                {LTL_THRESHOLDS.MAX_WEIGHT_LBS.toLocaleString()} lbs, or{' '}
                {LTL_THRESHOLDS.MAX_LINEAR_FEET} linear feet may not qualify for
                LTL pricing.
              </Typography>
              <ul className='text-xs mt-1 space-y-0.5'>
                {warnings.exceedsLinearFeet && (
                  <li>
                    • Linear feet: {metrics.linearFeet} ft (max{' '}
                    {LTL_THRESHOLDS.MAX_LINEAR_FEET})
                  </li>
                )}
                {warnings.exceedsCubicFeet && (
                  <li>
                    • Cubic feet: {metrics.totalCubicFeet.toFixed(1)} ft³ (max{' '}
                    {LTL_THRESHOLDS.MAX_CUBIC_FEET})
                  </li>
                )}
                {warnings.exceedsWeight && (
                  <li>
                    • Weight: {Math.round(metrics.totalWeight).toLocaleString()}{' '}
                    lbs (max {LTL_THRESHOLDS.MAX_WEIGHT_LBS.toLocaleString()})
                  </li>
                )}
              </ul>
              <Typography variant='body-xs' className='mt-2'>
                Freight must be crated or palletized for volume rates. Weights
                and dimensions must match the quote.
              </Typography>
            </Flex>
          </Flex>
        </div>
      )}
    </Flex>
  );
}
